import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, Container, Typography, Card, CardContent, 
  Button, Chip, Stack, Accordion, AccordionSummary, AccordionDetails,
  TextField, InputAdornment, Tabs, Tab, Paper, alpha, 
  Grid
} from '@mui/material';
import { 
  Devices, Speed, Layers, ExpandMore, ArrowForward, 
  Search, HelpOutline, AutoAwesome, MonetizationOn, 
  Security, RocketLaunch, Build, Compare, IntegrationInstructions 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const faqData = [
  {
    category: "Architecture & Performance",
    question: "What is hybrid and cross-platform app development?",
    answer: "Hybrid and cross-platform app development allows a single codebase to run across iOS, Android, and Web platforms. By using frameworks like Flutter, React Native, or modern PWAs, business logic, UI components, and API integrations are unified, drastically reducing development overhead compared to writing independent native apps."
  },
  {
    category: "Architecture & Performance",
    question: "How do modern Flutter and React Native compare to traditional web-wrapper apps (Ionic/Cordova)?",
    answer: "Legacy hybrid tools relied on rendering HTML/CSS inside web views, leading to laggy UI and sluggish performance. Modern cross-platform frameworks compile directly to native ARM code (Flutter via Dart AOT) or bridge directly to native UI widgets (React Native via Fabric), delivering near-native 60fps/120fps performance."
  },
  {
    category: "Architecture & Performance",
    question: "Can a cross-platform app achieve genuine 60fps or 120fps smooth animations?",
    answer: "Yes. When engineered properly with thread-safe state management, optimized list views, dynamic asset caching, and native GPU rendering engines (such as Flutter's Impeller engine), cross-platform applications render smoothly without visual stutter."
  },
  {
    category: "Architecture & Performance",
    question: "How are native device APIs (Biometrics, Camera, Bluetooth, NFC) accessed in cross-platform apps?",
    answer: "Frameworks connect to underlying OS capabilities via platform channels (Flutter MethodChannels / React Native Native Modules). If a pre-built community bridge isn't available, custom native code (Swift for iOS, Kotlin for Android) is written to interact directly with hardware."
  },
  {
    category: "Framework Comparison",
    question: "How do I choose between Flutter, React Native, and Progressive Web Apps (PWAs)?",
    answer: "Selection depends on your product goals: Choose Flutter for total UI control, custom animations, and identical cross-platform visual consistency. Choose React Native if you want native platform UI elements or have an existing React web team. Choose PWAs for instant web access without app store installation constraints."
  },
  {
    category: "Framework Comparison",
    question: "What are Progressive Web Apps (PWAs), and when should they be used?",
    answer: "PWAs are web applications that leverage modern browser APIs to offer app-like experiences, including offline caching, background sync, and push notifications. They are ideal for rapid market testing, B2B tools, or products where avoiding app store approval friction is critical."
  },
  {
    category: "Framework Comparison",
    question: "Will a cross-platform app look and feel native to both iOS and Android users?",
    answer: "Yes. Component libraries can dynamically render iOS Cupertino widgets on Apple devices and Material Design elements on Android, preserving platform-specific navigation patterns, back-button behaviors, and gesture cues."
  },
  {
    category: "Framework Comparison",
    question: "Are cross-platform frameworks suitable for enterprise-scale applications?",
    answer: "Absolutely. Companies like Google, Meta, Tesla, Alibaba, and Bloomberg rely on cross-platform stacks for their flagship apps. Proper architecture, micro-frontends, and automated CI/CD pipelines ensure enterprise-grade scalability."
  },
  {
    category: "Cost & Timeline",
    question: "How much cost savings does cross-platform development deliver compared to native?",
    answer: "A cross-platform strategy generally reduces initial development labor costs by 35% to 50% and slashes long-term maintenance costs in half by maintaining one shared codebase instead of two distinct native codebases."
  },
  {
    category: "Cost & Timeline",
    question: "What percentage of code is actually shared between iOS and Android?",
    answer: "Typically, 80% to 95% of business logic, state management, API service layers, and UI components are shared. Only highly specialized hardware integrations or platform-specific permissions require custom native code."
  },
  {
    category: "Cost & Timeline",
    question: "What is the average timeline to build and launch a hybrid MVP?",
    answer: "A feature-complete MVP typically takes between 6 to 12 weeks from initial architectural planning to App Store and Google Play store submission, depending on backend complexity and integration requirements."
  },
  {
    category: "Cost & Timeline",
    question: "Can we expand a hybrid mobile app to desktop (macOS/Windows) or web later?",
    answer: "Yes. Both Flutter and React Native (via React Native Web) support compilation to desktop and web binaries from the original mobile repository with responsive layout adjustments."
  },
  {
    category: "Security & Integration",
    question: "How is application security handled in cross-platform mobile apps?",
    answer: "Enterprise security standards are strictly enforced: encrypted local storage (Keychain/KeyStore), SSL Pinning for network requests, obfuscated release builds, biometric authentication, and secure token refresh cycles."
  },
  {
    category: "Security & Integration",
    question: "How is offline data synchronization managed in hybrid architectures?",
    answer: "Offline-first data layers utilize embedded fast key-value or relational stores (such as Hive, SQLite, or WatermelonDB) synchronized with backend REST or GraphQL endpoints via background background workers."
  },
  {
    category: "Security & Integration",
    question: "How are background tasks, geolocation, and push notifications handled?",
    answer: "Native background service managers and push notification services (Firebase Cloud Messaging, OneSignal) run hardware tasks in isolated background threads to minimize battery drain."
  },
  {
    category: "Security & Integration",
    question: "What automated testing procedures guarantee app stability across multiple devices?",
    answer: "Testing strategy includes unit testing for core business logic, widget/component snapshot testing, and automated integration end-to-end tests run across real cloud device matrices."
  },
  {
    category: "Process & Delivery",
    question: "Do you assist with Apple App Store and Google Play Store submission and compliance?",
    answer: "Yes. End-to-end service includes app bundle signing, store listing configuration, privacy policy setup, test track distribution (TestFlight/Play Console Internal Testing), and navigating store review guidelines."
  },
  {
    category: "Process & Delivery",
    question: "Can an existing native app be converted into a cross-platform app incrementally?",
    answer: "Yes. Modern frameworks support 'Add-to-App' integration, allowing us to embed cross-platform views inside an existing native application screen-by-screen without a complete rewrite."
  },
  {
    category: "Process & Delivery",
    question: "How are software updates and Over-The-Air (OTA) patches deployed?",
    answer: "For React Native and PWA solutions, OTA update platforms (such as Expo Updates or CodePush) allow critical bug fixes and JS bundle updates to be pushed instantly to users without waiting for app store reviews."
  },
  {
    category: "Process & Delivery",
    question: "Who owns the source code and intellectual property (IP)?",
    answer: "You maintain 100% ownership of all repository source code, design assets, API configurations, and intellectual property upon delivery."
  },
  {
    category: "Process & Delivery",
    question: "How do we request a platform evaluation for our mobile product?",
    answer: "Click the CTA button to schedule an initial scoping session. We will evaluate your product requirements, budget, and performance needs to recommend the optimal framework (Flutter, React Native, or PWA)."
  }
];

const HybridAppDevelopment = () => {
  const navigate = useNavigate();

  const siteUrl = "https://vikashtiwaridev.web.app/services/hybrid-app-development";
  const pageTitle = "Strategic Hybrid & Cross-Platform App Development | Vikash Tiwari";
  const pageDescription = "Maximize mobile product reach with custom cross-platform software engineering. Objective evaluation between Flutter, React Native, and PWAs for optimal TCO.";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Hybrid & Cross-Platform Application Development",
    "serviceType": "Mobile Application Development",
    "provider": {
      "@type": "Person",
      "name": "Vikash Tiwari",
      "url": "https://vikashtiwaridev.web.app/"
    },
    "areaServed": "Worldwide",
    "description": pageDescription
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const categories = ['All', 'Architecture & Performance', 'Framework Comparison', 'Cost & Timeline', 'Security & Integration', 'Process & Delivery'];

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeTab === 'All' || item.category === activeTab;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeTab]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const advantages = [
    { 
      title: "Accelerated Time-to-Market", 
      desc: "Deploy to iOS, Android, and Web simultaneously using single-source framework architecture and automated CI/CD build pipelines.",
      icon: <Speed sx={{ fontSize: 32, color: '#00E5FF' }} />
    },
    { 
      title: "Up to 40% Cost Reduction", 
      desc: "Slash development budgets and long-term engineering costs by replacing dual native dev pipelines with a unified core team.",
      icon: <MonetizationOn sx={{ fontSize: 32, color: '#7C4DFF' }} />
    },
    { 
      title: "Objective Framework Guidance", 
      desc: "Independent technical assessment determining whether Flutter, React Native, or PWA best suits your performance goals.",
      icon: <Compare sx={{ fontSize: 32, color: '#00E5FF' }} />
    },
    { 
      title: "Unified Business Logic", 
      desc: "Centralize database models, state management, and API validation layers while preserving platform-specific UI interaction styles.",
      icon: <Layers sx={{ fontSize: 32, color: '#7C4DFF' }} />
    }
  ];

  const metrics = [
    { value: "40%", label: "Lower TCO" },
    { value: "95%+", label: "Shared Codebase" },
    { value: "2x", label: "Faster Launch" },
    { value: "100%", label: "IP Ownership" }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="icon" type="image/x-icon" href="https://vikashtiwaridev.web.app/VikashTiwariIcon.png" />
        <meta name="keywords" content="Hybrid App Development, Cross Platform Mobile Apps, Multiplatform Software Engineering, Flutter vs React Native, PWA Development" />
        <link rel="canonical" href={siteUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Box component="main" sx={{ bgcolor: '#0A0E17', color: '#F1F5F9', minHeight: '100vh', overflowX: 'hidden' }}>
        
        {/* ================= HERO SECTION ================= */}
        <Box 
          sx={{ 
            position: 'relative', 
            pt: { xs: 10, md: 16 }, 
            pb: { xs: 10, md: 14 },
            background: 'radial-gradient(circle at 50% 10%, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0.08) 45%, rgba(10, 14, 23, 1) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
              width: { xs: '300px', md: '600px' }, height: { xs: '300px', md: '600px' },
              background: 'radial-gradient(circle, rgba(124, 77, 255, 0.15) 0%, rgba(0, 229, 255, 0) 70%)',
              filter: 'blur(80px)', pointerEvents: 'none'
            }} 
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: '880px', mx: 'auto', textAlign: 'center' }}>
              
              <Chip 
                icon={<AutoAwesome sx={{ fontSize: '18px !important', color: '#00E5FF' }} />} 
                label="Multi-Platform Software Strategy" 
                sx={{ 
                  mb: 3, 
                  py: 2, 
                  px: 1,
                  bgcolor: alpha('#00E5FF', 0.08), 
                  color: '#00E5FF', 
                  border: '1px solid rgba(0, 229, 255, 0.3)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  backdropFilter: 'blur(10px)'
                }} 
              />

              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' }, 
                  fontWeight: 900, 
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                High-Performance Solutions for{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Hybrid & Cross-Platform Apps
                </Box>
              </Typography>

              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#94A3B8', 
                  fontSize: { xs: '1.1rem', md: '1.35rem' }, 
                  fontWeight: 400, 
                  lineHeight: 1.6, 
                  mb: 5,
                  maxWidth: '760px',
                  mx: 'auto'
                }}
              >
                Maximize audience reach and optimize development budgets through data-driven cross-platform architecture. 
                Seamless deployment across iOS, Android, and Web.
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ mb: 8 }}
              >
                <Button 
                  variant="contained" 
                  size="large" 
                  endIcon={<ArrowForward />} 
                  onClick={() => navigate('/contact')}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #7C4DFF 0%, #00E5FF 100%)',
                    boxShadow: '0px 8px 24px rgba(124, 77, 255, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #6B38FF 0%, #00D2EE 100%)',
                      boxShadow: '0px 12px 32px rgba(124, 77, 255, 0.5)',
                    }
                  }}
                >
                  Request Platform Evaluation
                </Button>

                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('faq-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#F1F5F9',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: '#00E5FF',
                      bgcolor: alpha('#00E5FF', 0.05)
                    }
                  }}
                >
                  Explore Technical FAQs (21)
                </Button>
              </Stack>

              {/* Stats Bar */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {metrics.map((m, idx) => (
                  <Grid item xs={6} sm={3} key={idx}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        borderRadius: '16px', 
                        bgcolor: 'rgba(15, 23, 42, 0.6)', 
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)'
                      }}
                    >
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 800, 
                          background: 'linear-gradient(90deg, #00E5FF, #7C4DFF)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5
                        }}
                      >
                        {m.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                        {m.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

            </Box>
          </Container>
        </Box>

        {/* ================= CORE ARCHITECTURE BENEFITS (2x2 Grid) ================= */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' }, 
                fontWeight: 800, 
                mb: 2,
                letterSpacing: '-0.01em'
              }}
            >
              Hybrid Development Advantages
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '650px', mx: 'auto', fontSize: '1.1rem' }}>
              Optimizing engineering velocity and software maintainability through unified multi-platform architecture.
            </Typography>
          </Box>

          {/* Strict 2x2 CSS Grid Layout */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              gap: 7 
            }}
          >
            {advantages.slice(0, 4).map((item, i) => (
              <Card 
                key={i}
                elevation={0}
                sx={{ 
                  height: '100%', 
                  borderRadius: '20px', 
                  bgcolor: 'rgba(15, 23, 42, 0.5)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  p: 2,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(0, 229, 255, 0.4)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                    bgcolor: 'rgba(15, 23, 42, 0.8)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box 
                    sx={{ 
                      width: 56, height: 56, borderRadius: '14px', 
                      bgcolor: 'rgba(255, 255, 255, 0.03)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontSize: '1.35rem', fontWeight: 700, mb: 1.5, color: '#F8FAFC' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', lineHeight: 1.7, fontSize: '0.975rem' }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>

        {/* ================= SUPPORTED FRAMEWORKS & STACK ================= */}
        <Box 
          sx={{ 
            py: { xs: 8, md: 10 }, 
            bgcolor: 'rgba(15, 23, 42, 0.7)', 
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(10, 14, 23, 0.8) 100%)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, mb: 2 }}>
                Supported Cross-Platform Frameworks & Tools
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                Leveraging the right toolchain based on your product's performance and scalability profile.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ gap: 1.5 }}>
              {[
                "Flutter", "React Native", "Progressive Web Apps (PWA)", "Dart", "TypeScript", 
                "React.js", "Expo", "Redux / Toolkit", "BLoC Pattern", "REST & GraphQL APIs", 
                "Firebase", "SQLite / Hive", "Native Modules", "Codemagic", "Fastlane", "Jest & Integration Tests"
              ].map((tech) => (
                <Chip 
                  key={tech} 
                  label={tech} 
                  sx={{ 
                    py: 2.5, px: 2, 
                    fontSize: '0.95rem', 
                    fontWeight: 600,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    color: '#E2E8F0',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha('#00E5FF', 0.1),
                      borderColor: '#00E5FF',
                      color: '#00E5FF'
                    }
                  }} 
                />
              ))}
            </Stack>
          </Container>
        </Box>

        {/* ================= DYNAMIC FAQ SECTION (21 Questions with Search & Category Tabs) ================= */}
        <Container maxWidth="lg" id="faq-section" sx={{ py: { xs: 8, md: 12 } }}>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip 
              icon={<HelpOutline sx={{ fontSize: '18px !important', color: '#7C4DFF' }} />} 
              label="Technical & Architectural FAQs" 
              sx={{ mb: 2, bgcolor: alpha('#7C4DFF', 0.1), color: '#7C4DFF', fontWeight: 600 }} 
            />
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto' }}>
              Clear insights into platform trade-offs, development timelines, security standards, and framework selection.
            </Typography>
          </Box>

          {/* Search Bar & Category Filter */}
          <Box sx={{ maxWidth: '800px', mx: 'auto', mb: 5 }}>
            <TextField
              fullWidth
              placeholder="Search questions (e.g., Flutter vs React Native, cost, native APIs, PWA)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#00E5FF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  bgcolor: 'rgba(15, 23, 42, 0.8)',
                  color: '#F8FAFC',
                  fieldset: { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#00E5FF' },
                  '&.Mui-focused fieldset': { borderColor: '#7C4DFF' }
                }
              }}
            />

            <Tabs 
              value={activeTab} 
              onChange={(e, newVal) => setActiveTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#00E5FF', height: '3px', borderRadius: '3px' },
                '& .MuiTab-root': { 
                  color: '#94A3B8', 
                  fontWeight: 600, 
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  '&.Mui-selected': { color: '#00E5FF' }
                }
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Box>

          {/* Accordions */}
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const panelId = `panel-${index}`;
                return (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === panelId}
                    onChange={handleAccordionChange(panelId)}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.6)',
                      color: '#F1F5F9',
                      mb: 2,
                      borderRadius: '14px !important',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        borderColor: 'rgba(0, 229, 255, 0.3)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#00E5FF' }} />}
                      sx={{
                        p: 2.5,
                        '& .MuiAccordionSummary-content': { my: 0 }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, pr: 2 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, pt: 0, color: '#94A3B8', lineHeight: 1.7 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.975rem' }}>
                        {faq.answer}
                      </Typography>
                      <Chip 
                        label={faq.category} 
                        size="small" 
                        sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748B', fontSize: '0.75rem' }} 
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#64748B' }}>
                <Typography variant="h6">No matching questions found.</Typography>
                <Typography variant="body2">Try adjusting your search criteria or selecting a different category.</Typography>
              </Box>
            )}
          </Box>

        </Container>

        {/* ================= CALL TO ACTION BANNER ================= */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 5, md: 8 },
              borderRadius: '28px',
              background: 'linear-gradient(135deg, rgba(124, 77, 255, 0.2) 0%, rgba(0, 229, 255, 0.1) 100%)',
              border: '1px solid rgba(0, 229, 255, 0.2)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, mb: 2 }}>
              Ready to Evaluate Your Cross-Platform Architecture?
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
              Schedule a technical assessment session to determine the optimal cross-platform strategy for your business goals.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/contact')}
              sx={{
                py: 1.8,
                px: 5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: '12px',
                textTransform: 'none',
                background: '#F8FAFC',
                color: '#0A0E17',
                '&:hover': {
                  background: '#FFFFFF',
                  boxShadow: '0px 8px 24px rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Get Started
            </Button>
          </Paper>
        </Container>

      </Box>
    </>
  );
};

export default HybridAppDevelopment;